import { Array, Object, Optional, Static, String, Union, Literal, Enum, Number } from '@sinclair/typebox';

import { BoundFlight } from '../../flightOffers';
import { Passenger, PassengerCounts } from '../../sas-domain/passengers';
import { PaymentType } from '../../sas-domain/payment';
import { Coupon } from '../../sas-domain/pointsPricing';

export const AwardOrderRequestParams = Object({
  id: String(),
});

export const AwardOrderRequestBody = Object({
  cartId: String(),
});

export type AwardOrderRequestBody = Static<typeof AwardOrderRequestBody>;

const SelectedBound = Object({
  flight: BoundFlight,
  selectorId: String(),
});

export enum OneTimePasswordStatus {
  Pending = 'Pending',
  Succeeded = 'Succeeded',
}

export enum AwardOrderStatusType {
  PENDING = 'booking-pending',
  SUCCESS = 'booking-success',
  FAIL_NOT_REFUNDED = 'booking-failed-not-refunded',
  FAIL_REFUNDED = 'booking-failed-refunded',
  OTP_PENDING = 'otp-pending',
  OTP_FAILED = 'otp-failed',
}

export const AwardOrderStatus = Object({ status: Enum(AwardOrderStatusType) });

export type AwardOrderStatus = Static<typeof AwardOrderStatus>;

export const AwardOrder = Object({
  id: String(),
  status: Enum(AwardOrderStatusType),
  pnr: String(),
  pos: String(),
  officeId: String(),
  passengers: Array(Passenger),
  selectedCouponCodes: Array(String()),
  coupons: Array(Coupon),
  outbound: SelectedBound,
  inbound: Optional(SelectedBound),
  paymentReference: String(),
  oneTimePasswordStatus: Union([Literal(OneTimePasswordStatus.Pending), Literal(OneTimePasswordStatus.Succeeded)]),
});

export type AwardOrder = Static<typeof AwardOrder>;

export enum FarePriceErrorType {
  Mismatch = 'Mismatch',
  RedemptionDisabled = 'RedemptionDisabled',
  InsufficientPoints = 'InsufficientPoints',
  PayloadError = 'PayloadError',
}

export type AwardVerifyFarePriceError = Static<typeof AwardVerifyFarePriceError>;
export const AwardVerifyFarePriceError = Object({
  bookingErrorType: Union([
    Literal('UNKNOWN'),
    Literal(FarePriceErrorType.Mismatch),
    Literal(FarePriceErrorType.RedemptionDisabled),
    Literal(FarePriceErrorType.InsufficientPoints),
    Literal(FarePriceErrorType.PayloadError),
  ]),
});

export type AwardOrderSummary = Static<typeof AwardOrderSummary>;
export const AwardOrderSummary = Object({
  id: String(),
  status: Enum(AwardOrderStatusType),
  pnr: String(),
  pos: String(),
  passengerCounts: PassengerCounts,
  outbound: SelectedBound,
  inbound: Optional(SelectedBound),
  paymentInfo: Object({
    type: Enum(PaymentType),
    subType: String(),
    currency: String(),
    amount: Number(),
    maskedCardNumber: String(),
    pointsDeducted: Number(),
  }),
});

export type MmbConfirmationPaymentInfo = Static<typeof MmbConfirmationPaymentInfo>;
export const MmbConfirmationPaymentInfo = Object({
  totalPoints: Number(),
  discountedPoints: Optional(Number()),
  currency: String(),
  totalTaxes: Number(),
  taxBreakdown: Optional(
    Array(
      Object({
        name: String(),
        amount: Number(),
      })
    )
  ),
});
